import { GridColDef } from "@mui/x-data-grid";
import ExpirationDate from "../ExpirationDate/ExpirationDate";
import { alignLeft } from "../TableActions/TableActions";
import { formatDate, isEmpty, yesNo } from "../DetailView/DetailView";

// Columnas con el mismo formato en todas las tablas. Usan los mismos
// formateadores que el modal de información (DetailView), así una fecha,
// un "Sí/No" o un importe se ven igual en la tabla y en la ficha.

// searchValue: texto que usa el buscador de la tabla (lo que se ve en la celda)
export type AedasColDef = GridColDef & { searchValue?: (row: any) => any };

type ColumnOptions = Partial<AedasColDef>;

const base = (field: string, headerName: string, options: ColumnOptions = {}): AedasColDef => ({
  field,
  headerName,
  width: 150,
  flex: 1,
  editable: false,
  ...options,
});

// Valor vacío común a todas las celdas
const empty = <span className="cellEmpty">—</span>;
const orEmpty = (value: any) => (isEmpty(value) ? empty : value);

export const idColumn = (): AedasColDef => ({ field: "id", headerName: "ID", width: 70, hide: true });

// Columna principal (nombre del elemento), alineada a la izquierda
export const nameColumn = (field: string, headerName: string, options?: ColumnOptions) =>
  base(field, headerName, { flex: 2, ...alignLeft, ...options });

export const textColumn = (field: string, headerName: string, options?: ColumnOptions) =>
  base(field, headerName, { renderCell: (params) => orEmpty(params.value), ...options });

export const dateColumn = (field: string, headerName: string, options?: ColumnOptions) =>
  base(field, headerName, {
    renderCell: (params) => orEmpty(formatDate(params.value)),
    searchValue: (row) => formatDate(row[field]),
    ...options,
  });

// Fecha de vencimiento coloreada (vencida / aviso / vigente)
export const expirationColumn = (field = "expiration_day", headerName = "Fecha de vencimiento", options?: ColumnOptions) =>
  base(field, headerName, {
    renderCell: (params) => <ExpirationDate date={params.value} adviseDays={params.row.advise_days} />,
    searchValue: (row) => formatDate(row[field]) ?? "Sin vencimiento",
    ...options,
  });

export const yesNoColumn = (field: string, headerName: string, options?: ColumnOptions) =>
  base(field, headerName, {
    renderCell: (params) => orEmpty(yesNo(params.value)),
    searchValue: (row) => yesNo(row[field]),
    ...options,
  });

export const daysColumn = (field: string, headerName: string, options?: ColumnOptions) =>
  base(field, headerName, {
    renderCell: (params) =>
      isEmpty(params.value) || Number(params.value) === 0 ? empty : `${params.value} días antes`,
    ...options,
  });

export const currencyColumn = (field: string, headerName: string, options?: ColumnOptions) =>
  base(field, headerName, {
    renderCell: (params) => (isEmpty(params.value) ? empty : `${params.value} €`),
    ...options,
  });

// Muestra el nombre de un id buscándolo en una lista (partner, host, tipo de licencia...)
export const lookupColumn = (field: string, headerName: string, list: any[] | null, options?: ColumnOptions) => {
  const nameOf = (id: any) => list?.find((item: any) => item.id === id)?.name;
  return base(field, headerName, {
    // valueGetter: ordenar por el nombre y no por el id
    valueGetter: (params) => nameOf(params.row[field]),
    renderCell: (params) => orEmpty(params.value),
    searchValue: (row) => nameOf(row[field]),
    ...options,
  });
};

// Partner / Fabricante en una sola columna: "Partner", "Fabricante" o "Partner y fabricante"
export const partnerTypeLabel = (row: any) =>
  row?.is_part && row?.is_maker ? "Partner y fabricante" : row?.is_part ? "Partner" : row?.is_maker ? "Fabricante" : null;

export const partnerTypeColumn = (options?: ColumnOptions) =>
  base("partner_type", "Tipo", {
    valueGetter: (params) => partnerTypeLabel(params.row),
    renderCell: (params) => orEmpty(params.value),
    searchValue: partnerTypeLabel,
    ...options,
  });

// Texto normalizado (sin mayúsculas ni tildes) para comparar en el buscador
export const normalizeText = (value: any) =>
  String(value ?? "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase();

// Texto de una fila en las columnas visibles, tal y como se muestran
export const rowSearchText = (row: any, columns: GridColDef[]) =>
  normalizeText(
    columns
      .filter((col) => !col.hide && col.field !== "actions")
      .map((col) => {
        const searchValue = (col as AedasColDef).searchValue;
        return searchValue ? searchValue(row) : row[col.field];
      })
      .join(" ")
  );
